// =============================================================================
// complianceDoorSet.js — THE list of kiosk hosts whose corrective /
// emergency-fridge case writes go through the 1440 case doors instead of the
// direct table route. Extracted out of complianceSync.js (Lane CO, 16 Sep
// 2026) for exactly the reason eodDropSet.js exists (see that file):
//
//   * the TABLET (src/lib/complianceSync.js, in the browser bundle) — decides
//     whether a raise / close / lookup goes through the 1440 door or today's
//     direct table call;
//   * the DAILY LOCK-GATE (tools/lane_co_compliance_lock_gate.mjs, in Node) —
//     has to know which shops are SUPPOSED to be on the door, so it can tell
//     "a listed shop is still writing the old way" (a real breach of the gate
//     on migration 1441) apart from "a shop nobody has told to move yet"
//     (expected, and not a breach of anything).
//
// If those two ever disagreed — say, a host added to the tablet's list but
// not to the gate's — a shop could be reported as breaching a gate nobody had
// actually asked it to meet yet. One list, two readers, never two lists.
//
// IT HAS NO IMPORTS ON PURPOSE. The Node-side gate loads this file directly,
// so it must not drag in the Supabase browser client (or anything else).
//
// SHIPS EMPTY, BYTE-IDENTICAL TO WHAT complianceSync.js DEFINED INLINE BEFORE
// TONIGHT. Nothing is switched over by this move. A go-live is still a
// reviewed one-line add here, one kiosk at a time, and only once migration
// 1440 is on production (complianceSync.js explains what breaks if a host is
// added before then — the raise is refused outright and the close silently
// does nothing).
// =============================================================================

// The kiosk hostnames whose case writes (corrective actions, emergency-fridge
// incidents) go through the 1440 doors. Empty today — every kiosk keeps using
// the direct table route until a host is added here in daylight.
export const COMPLIANCE_CASE_DOOR_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- (the kitchen is on the Trafford host since 24 Sep 2026 — was mcr-kitchen) added ONLY once 1440 is on production, one host at a time
]);

// Which shop each door hostname is. Kept here rather than derived from the
// hostname — same reasoning as eodDropSet.js's HOST_TO_SITE. "mcr" for the
// kitchen is MEASURED, not guessed: complianceSync.js's own header flagged an
// open question ("the kitchen app sends site 'mcr' and the old backend
// normalises it to 'kitchen' — confirm which key the sites table uses"); Lane
// CO checked staging directly on 16 Sep 2026 (`select key, name from
// public.sites`) and the kitchen's row is keyed **'mcr'**. Arndale is CLOSED
// (16 Aug 2026, see eodDropSet.js) and is never listed here.
const HOST_TO_SITE = {
  "mcr-kitchen.eatrolled.com": "mcr",
  "trafford.eatrolled.com": "tc",
  "meadowhall.eatrolled.com": "meadowhall",
};

/** The shop KEY for a compliance door hostname, or null if it is not one we know. */
export function siteKeyForDoorHost(host) {
  return HOST_TO_SITE[String(host || "").toLowerCase()] || null;
}

/**
 * The same list, as shop KEYS — what the daily lock-gate cross-checks against
 * tools/lane_ca_door_coverage.sql's per-shop rows. Derived from
 * COMPLIANCE_CASE_DOOR_HOSTS so the two can never drift: adding the host above
 * is the whole change. An unmapped hostname is dropped from `keys` AND named
 * in `unknown`, so a typo shows up as a loud gap rather than a silently
 * un-checked shop.
 */
export function complianceDoorSiteKeys() {
  const keys = [];
  const unknown = [];
  for (const host of COMPLIANCE_CASE_DOOR_HOSTS) {
    const key = siteKeyForDoorHost(host);
    if (key) { if (!keys.includes(key)) keys.push(key); }
    else unknown.push(host);
  }
  return { keys, unknown };
}
